import { FC, useState } from 'react';
import {
  Box,
  Grid,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useUsersByCategoryAdminHook } from '../../../hooks/useUsersByCategoryAdminHook';
import CategoryUsersAdminView from './categoryUsersAdmin.view';
import { UserByCategoryItem } from '../../../apis/users/users.interfaces';
import MainTitle from "../../components/mainTitle";

enum UserCategory {
  VISITOR = 'visitor',
  GUARDIAN = 'guardian',
  STAFF = 'staff',
  EXTERNAL = 'external',
  STUDENT = 'student',
}

const CATEGORY_LABELS: Record<UserCategory, string> = {
  [UserCategory.VISITOR]: 'Visitor',
  [UserCategory.GUARDIAN]: 'Guardian',
  [UserCategory.STAFF]: 'Staff',
  [UserCategory.EXTERNAL]: 'External',
  [UserCategory.STUDENT]: 'Student',
};

const CATEGORY_ORDER: UserCategory[] = [
  UserCategory.VISITOR,
  UserCategory.GUARDIAN,
  UserCategory.STAFF,
  UserCategory.EXTERNAL,
  UserCategory.STUDENT,
];

interface CategoryTabPanelProps {
  category: UserCategory;
  search: string;
}

const CategoryTabPanel: FC<CategoryTabPanelProps> = ({ category, search }) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { data, isLoading } = useUsersByCategoryAdminHook({
    category,
    page,
    limit,
    search: search || undefined,
  });

  const users: UserByCategoryItem[] = data?.data ?? [];
  const total = data?.count ?? 0;

  return (
    <CategoryUsersAdminView
      users={users}
      isLoading={isLoading}
      total={total}
      page={page}
      limit={limit}
      onPageChange={setPage}
      onLimitChange={(newLimit) => {
        setLimit(newLimit);
        setPage(1);
      }}
    />
  );
};

const UsersAdminView: FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');

  const currentCategory = CATEGORY_ORDER[activeTab] ?? UserCategory.VISITOR;

  return (
    <Grid container sx={{ height: '100%', p: 3 }} data-testid="users-admin-container">
      <Grid item xs={12} sx={{ mb: 2, height: '60px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <MainTitle title="User Management" />
          <TextField
            size="small"
            placeholder="Search users…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 260 }}
            data-testid="users-admin-search"
          />
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ height: 'calc(100% - 60px)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              setSearch('');
            }}
            data-testid="users-admin-tabs"
          >
            {CATEGORY_ORDER.map((cat) => (
              <Tab key={cat} label={CATEGORY_LABELS[cat]} data-testid={`tab-${cat}`} />
            ))}
          </Tabs>
        </Box>

        <Box data-testid={`users-admin-tab-panel-${currentCategory}`}>
          <CategoryTabPanel
            key={currentCategory}
            category={currentCategory}
            search={search}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default UsersAdminView;
